/**
 * useStealthSightings - the GM's question when the opposition could see someone
 * (plan map-display, H; engine/stealth/sight).
 *
 * In Stealth, on the director's screen (the GM, or anyone solo): whenever a
 * party member comes within a watcher's reach (by the member's light, the
 * watcher's Dark Sight) with nothing in the way, the pair is queued as a
 * SIGHTING. The GM answers each: Roll it (a call for the member's
 * Concealment against the nearest watcher's Awareness), Seen (the ladder one
 * rung up), or Missed. ONE question per character (2026-10-28: one discovered
 * brother produced dozens): every watcher who could see them is listed, and
 * Seen is one rung. A character still in sight of anyone is not asked again;
 * one seen stays seen a metre past the reach, so stepping across its edge
 * asks nothing; one who leaves sight and comes back is new. AT ALARM nothing
 * is asked and the questions waiting go - they are found already (a close-in
 * the last Seen offered stays: it is a move, not a question) - while who is in
 * sight is still kept, so stepping down from Alarm asks nothing anew. The
 * display never detects alone. Seen offers the GM the move that follows -
 * the watcher closing on them, stopping in reach (Z2) - made only when the
 * GM says Move them.
 *
 * @param {object} s - the shell scope: direction, stealth (useShellStealth), roster, tokens, terrain, actor,
 *   testCalls (useShellTestCalls).
 * @returns {{ sightings: { current: object|null, rollIt: Function, seen: Function, missed: Function,
 *   proposal: object|null, moveThem: Function, leaveThem: Function } }}
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { lightAt } from '../engine/battlefield/lighting';
import { blocksLineOfSight } from '../engine/battlefield/lineOfSight';
import { moveToken, tokenEye } from '../engine/battlefield/tokens';
import { byTarget, freshTargets, sightingCall, sightings as sightingsOf } from '../engine/stealth/sight';
import { placedParty } from '../engine/stealth/opposition';
import { stepRung } from '../engine/stealth/ladder';
import { closeIn } from '../engine/stealth/closeIn';

/**
 * Who is in sight of whom now, on this map; those seen already (`held`) a metre further. Null when there is no map
 * to ask yet (it is still loading).
 */
function inSightNow({ stealth, roster, tokens, terrain, solids }, held) {
  if (!terrain) return null;
  const watching = stealth.watching.filter((w) => tokens?.[w.id]).map((w) => ({ ...w, at: tokens[w.id] }));
  const party = placedParty(roster, stealth.opposition, tokens);
  const blocked = (w, t) => blocksLineOfSight(tokenEye(tokens[w]), tokenEye(tokens[t]), solids);
  return sightingsOf(watching, party, (at) => lightAt(terrain, at.x, at.z) === 'lit', blocked, held);
}

/**
 * The characters waiting for the GM, oldest first: each is queued as they come into anyone's sight, once. A screen
 * that mounts in Stealth (a reload) only LEARNS what is in sight on its first look - those were asked about before;
 * entering Stealth asks about everyone there; leaving it drops what was waiting. `quiet` (Alarm) drops what waited
 * and asks nothing, still learning who is in sight. `now` is null while there is nothing to look at: what is known
 * stays known. `inSight` (target ids, null before the first look) is the caller's, which holds them a metre on.
 */
function useSightingQueue(now, watching, quiet, inSight) {
  const [queue, setQueue] = useState([]);
  useEffect(() => {
    if (watching) return;
    // Out of Stealth nothing is in sight: entering it asks about everyone there. Only a screen that MOUNTS in
    // Stealth (a reload) has an unknown past, and its first look only learns.
    setQueue([]);
    inSight.current = new Set();
  }, [watching, inSight]);
  useEffect(() => {
    if (quiet) setQueue([]);
  }, [quiet]);
  useEffect(() => {
    if (!now) return;
    const fresh = inSight.current && !quiet ? freshTargets(inSight.current, now) : [];
    inSight.current = new Set(now.map((s) => s.targetId));
    if (fresh.length) setQueue((q) => [...q, ...fresh.filter((f) => !q.some((x) => x.targetId === f.targetId))]);
  }, [now, quiet, inSight]);
  const next = useCallback(() => setQueue((q) => q.slice(1)), []);
  return { queue, next };
}

/** The question on screen: the waiting character, with the watchers who see them NOW when anyone still does. */
function questionOf(queue, now, nameOf) {
  const waiting = queue[0];
  if (!waiting) return null;
  const watchers = byTarget(now ?? []).find((t) => t.targetId === waiting.targetId)?.watchers ?? waiting.watchers;
  const [first, ...others] = watchers;
  return {
    ...first,
    watcher: nameOf(first.watcherId),
    target: nameOf(waiting.targetId),
    also: others.map((o) => nameOf(o.watcherId)),
    more: queue.length - 1,
  };
}

export default function useStealthSightings(s) {
  const { stealth, roster, tokens, terrain } = s;
  const watching = s.direction?.frame?.phase === 'stealth' && !!stealth?.canDirect;
  // At Alarm they are found already: nothing more to ask (2026-10-28).
  const quiet = stealth?.state?.rung === 'alarm';
  // Not the whole stealth object: it changes with every line typed on the ladder.
  const { watching: seers, opposition } = stealth;
  // The shell's own meshed map (useShellLiveMap), not a fresh mesh per token move.
  const solids = s.mapSolids;
  // Who is in sight, by character: the queue keeps it, and the next look holds them a metre further.
  const inSight = useRef(null);
  const now = useMemo(
    () =>
      watching
        ? inSightNow(
            { stealth: { watching: seers, opposition }, roster, tokens, terrain, solids: solids ?? [] },
            inSight.current ?? new Set(),
          )
        : null,
    [watching, seers, opposition, roster, tokens, terrain, solids],
  );
  const { queue, next } = useSightingQueue(now, watching, quiet, inSight);
  // Memoized: Roll it and Seen are made from it, and a new question every render made them new every render.
  const shown = useMemo(
    () => questionOf(queue, now, (id) => roster.find((c) => c.id === id)?.name ?? '?'),
    [queue, now, roster],
  );
  const live = useRef(s);
  live.current = s;
  const rollIt = useCallback(() => {
    if (shown) live.current.testCalls?.callFor(sightingCall(shown));
    next();
  }, [shown, next]);
  // Not quieted at Alarm: the Seen that raised it may offer the watcher's close-in (the review, 2026-10-28).
  const { proposal, propose, moveThem, leaveThem } = useCloseIn(live, watching);
  const seen = useCallback(() => {
    const st = live.current.stealth;
    st.setRung(stepRung(st.state.rung, 1));
    // Seen: the nearest watcher may close on them - offered to the GM, never made (Z2).
    if (shown) propose(shown.watcherId, shown.targetId);
    next();
  }, [next, shown, propose]);
  // Memoized: the GM's map draws it, and a new object on every shell render would redraw the ghost each time.
  const offered = useMemo(() => {
    const named = (id) => roster.find((c) => c.id === id)?.name ?? '?';
    return proposal ? { ...proposal, mover: named(proposal.moverId), toward: named(proposal.towardId) } : null;
  }, [proposal, roster]);
  return { sightings: { current: shown, rollIt, seen, missed: next, proposal: offered, moveThem, leaveThem } };
}

/**
 * THE MOVE THAT FOLLOWS A SIGHTING (plan design-gaps, Z2; the ruling: only the GM moves contacts): a watcher seen to
 * see someone may close on them, stopping in reach (engine/stealth/closeIn). Proposed on this - the director's -
 * screen alone: Move them writes the mover's token there (the shell's writer routes a hidden figure's), Leave them
 * drops it. Leaving Stealth drops it too.
 */
function useCloseIn(live, watching) {
  const [proposal, setProposal] = useState(null);
  useEffect(() => {
    if (!watching) setProposal(null);
  }, [watching]);
  const propose = useCallback(
    (moverId, towardId) => {
      const { tokens } = live.current;
      const [mover, toward] = [tokens?.[moverId], tokens?.[towardId]];
      setProposal(mover && toward ? closeIn(mover, toward) : null);
    },
    [live],
  );
  const moveThem = useCallback(() => {
    const { tokens, terrain } = live.current;
    const [token, toward] = proposal ? [tokens?.[proposal.moverId], tokens?.[proposal.towardId]] : [];
    // Worked out again from where they stand NOW: the target may have moved since Seen (the Track Z review). Moved
    // as every move is: inside the boundary, its rev bumped so the write wins the next merge.
    const stop = token && toward ? closeIn(token, toward) : null;
    if (stop) live.current.writeToken?.(moveToken(token, stop.at.x, stop.at.z, terrain.boundary), { immediate: true });
    setProposal(null);
  }, [live, proposal]);
  const leaveThem = useCallback(() => setProposal(null), []);
  return { proposal, propose, moveThem, leaveThem };
}
